<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import ConversationPriorityMenu from './ConversationPriorityMenu.vue';
import ConversationSnoozeMenu from './ConversationSnoozeMenu.vue';
import CreateLinkedTicketDialog from './CreateLinkedTicketDialog.vue';
import CaseCreateDialog from 'dashboard/components-next/SupportCases/CaseCreateDialog.vue';
import {
  conversationCaseTitle,
  conversationRequester,
  useCaseCreateDialog,
} from 'dashboard/composables/useCaseCreateDialog';
import Avatar from 'next/avatar/Avatar.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import ConversationCallButton from './ConversationCallButton.vue';
import StatusPill from 'dashboard/components-next/Conversation/StatusPill.vue';
import { deriveTicketState } from 'dashboard/helper/conversationLens';
import wootConstants from 'dashboard/constants/globals';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import {
  getContactChannelHandle,
  getInboxChannelHandle,
} from 'dashboard/helper/inbox';
import { CHAT_QUEUES } from 'dashboard/routes/dashboard/conversation/chatQueues';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

// A queue's conversation route is named `<queue route name>_conversation`, so
// the slug the back link needs is whichever queue owns the current route.
const queueSlugFor = routeName =>
  CHAT_QUEUES.find(({ name }) => `${name}_conversation` === routeName)?.slug;

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_participating: 'participating',
    conversation_through_unattended: 'unattended',
    conversation_through_ai_handling: 'ai_handling',
    conversation_through_resolved_by_ai: 'resolved_by_ai',
  };
  const path = conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    conversationType: conversationTypeMap[name],
    customViewId,
    tickets: name === 'tickets_conversation',
    queueSlug: queueSlugFor(name),
  });

  // The tickets screen keeps its active view in the query string, so the list
  // this returns to is only the list it was opened from with the view carried
  // over.
  return name === 'tickets_conversation' ? { path, query: route.query } : path;
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

// The two halves of "who is this and where did they reach us": the contact's
// own handle on this channel, and the business line it arrived on.
const contactHandle = computed(() =>
  getContactChannelHandle(currentContact.value, inbox.value)
);

const inboxHandle = computed(() => getInboxChannelHandle(inbox.value));

// Ticket chrome is gated on the record, not the route: a ticket opened from the
// chat inbox gets the same header as one opened from the tickets screen, so the
// two screens cannot disagree about what a ticket looks like.
const isTicket = computed(() =>
  Boolean(props.chat?.additional_attributes?.ticket)
);

// Same fallback chain the ticket rows use: the subject column, then the mail
// subject, then nothing rather than a truncated message body in a title slot.
const ticketSubject = computed(
  () =>
    props.chat?.subject ||
    props.chat?.additional_attributes?.mail_subject ||
    t('TICKETS.EMPTY.NO_SUBJECT')
);

// The whole chat goes in so the server-provided `ticket_state` wins over the
// local status x resolution fallback.
const ticketState = computed(() => deriveTicketState(props.chat));

const hasSlaPolicyId = computed(
  () => props.chat?.applied_sla?.id && !currentContact.value?.blocked
);

const { uiSettings, updateUISettings } = useUISettings();

// With both side panels closed the header is the only way back to Details.
const showOpenSidepanelButton = computed(
  () =>
    !uiSettings.value.is_contact_sidebar_open &&
    !uiSettings.value.is_copilot_panel_open
);

const openSidepanel = () => {
  updateUISettings({
    is_contact_sidebar_open: true,
    is_copilot_panel_open: false,
  });
};

const createTicketDialogRef = ref(null);

const linkedConversationId = computed(
  () => props.chat?.additional_attributes?.linked_conversation_id
);

const isSupportCasesEnabled = computed(() =>
  store.getters['accounts/isFeatureEnabledonAccount'](
    accountId.value,
    FEATURE_FLAGS.SUPPORT_CASES
  )
);

// The ticket engine supersedes the legacy conversation-lens tickets, so once
// `support_cases` is on the convert button stays hidden even while the old
// `tickets` flag is still set (see docs/ticket-engine/README.md).
const isLegacyTicketsEnabled = computed(
  () =>
    store.getters['accounts/isFeatureEnabledonAccount'](
      accountId.value,
      FEATURE_FLAGS.TICKETS
    ) && !isSupportCasesEnabled.value
);

// Any unlinked conversation that is not itself a ticket can be converted,
// regardless of channel.
const showCreateTicketButton = computed(
  () =>
    isLegacyTicketsEnabled.value &&
    !linkedConversationId.value &&
    !props.chat?.additional_attributes?.ticket
);

// The engine's quick action: the same dialog and escalation the Details
// panel's Tickets section runs, kept in the header so opening a ticket does
// not require the sidebar to be open.
const {
  dialogRef: caseCreateDialogRef,
  caseTypes,
  isSubmitting: isCreatingCase,
  openDialog: openCaseCreateDialog,
  onSubmit: onCaseCreate,
} = useCaseCreateDialog(() => props.chat.id);

const caseInitialTitle = computed(() => conversationCaseTitle(props.chat));

const caseRequesterContact = computed(() => conversationRequester(props.chat));

const copyConversationId = async () => {
  try {
    await copyTextToClipboard(String(props.chat.id));
    useAlert(t('CONVERSATION.HEADER.COPY_ID_SUCCESS'));
  } catch (error) {
    // error
  }
};
</script>

<template>
  <div
    ref="conversationHeader"
    class="flex flex-row gap-2 items-center justify-between flex-1 w-full min-w-0 px-3 py-2 h-12"
  >
    <div class="flex items-center justify-start max-w-full min-w-0 flex-1">
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        icon-only
        class="ltr:mr-1.5 rtl:ml-1.5"
      />
      <Avatar
        v-tooltip.bottom="inbox.name"
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="32"
        :status="currentContact.availability_status"
        :inbox="inbox"
        hide-offline-status
      />
      <div
        class="flex flex-col items-start min-w-0 ml-2 overflow-hidden rtl:ml-0 rtl:mr-2"
      >
        <div class="flex flex-row items-center max-w-full gap-1 p-0 m-0">
          <span
            class="text-sm font-medium truncate leading-tight text-n-slate-12"
            :title="isTicket ? ticketSubject : currentContact.name"
          >
            {{ isTicket ? ticketSubject : currentContact.name }}
          </span>
          <fluent-icon
            v-if="!isHMACVerified"
            v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
            size="14"
            class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
            icon="warning"
          />
        </div>

        <div
          class="flex items-center gap-1.5 overflow-hidden text-xs conversation--header--actions text-n-slate-11 text-ellipsis whitespace-nowrap"
        >
          <button
            type="button"
            class="inline-flex items-center h-5 px-1.5 truncate transition-colors rounded-full cursor-pointer text-label-small text-n-slate-11 bg-n-alpha-2 hover:text-n-slate-12"
            @click="copyConversationId"
          >
            {{ `#${chat.id}` }}
          </button>
          <span v-if="isTicket" class="truncate">
            {{ currentContact.name }}
          </span>
          <span
            v-if="contactHandle"
            :title="contactHandle"
            class="inline-flex items-center h-5 px-1.5 truncate rounded-full text-label-small text-n-slate-11 bg-n-alpha-2"
          >
            {{ contactHandle }}
          </span>
          <InboxName
            v-if="hasMultipleInboxes"
            :inbox="inbox"
            class="!mx-0 h-5 px-1.5 rounded-full bg-n-alpha-2"
          />
          <span
            v-if="inboxHandle"
            :title="inboxHandle"
            class="inline-flex items-center h-5 px-1.5 truncate rounded-full text-label-small text-n-slate-11 bg-n-alpha-2"
          >
            {{ $t('CONVERSATION.HEADER.VIA', { source: inboxHandle }) }}
          </span>
          <router-link
            v-if="linkedConversationId"
            :to="`/app/accounts/${accountId}/conversations/${linkedConversationId}`"
            data-testid="linked-conversation-chip"
            class="inline-flex items-center h-5 gap-1 px-1.5 rounded-full bg-n-alpha-2 text-label-small text-n-slate-11 hover:text-n-slate-12"
          >
            <span class="i-lucide-link size-3" />
            #{{ linkedConversationId }}
          </router-link>
          <span
            v-if="isSnoozed"
            class="inline-flex items-center h-5 px-1.5 font-medium rounded-full bg-n-amber-3 text-n-amber-11"
          >
            {{ snoozedDisplayText }}
          </span>
        </div>
      </div>
    </div>
    <div
      class="flex flex-row items-center justify-end flex-shrink-0 gap-2 header-actions-wrap"
    >
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <StatusPill
        v-if="isTicket"
        :state="ticketState"
        class="hidden shrink-0 sm:inline-flex"
      />
      <ConversationCallButton :inbox="inbox" :chat="currentChat" />
      <NextButton
        v-if="showCreateTicketButton"
        v-tooltip="$t('TICKETS.CREATE_FROM_CONVERSATION.BUTTON_TOOLTIP')"
        :aria-label="$t('TICKETS.CREATE_FROM_CONVERSATION.BUTTON_TOOLTIP')"
        data-testid="create-linked-ticket-button"
        icon="i-lucide-ticket"
        ghost
        slate
        sm
        @click="createTicketDialogRef.open()"
      />
      <NextButton
        v-if="isSupportCasesEnabled"
        v-tooltip="$t('SUPPORT_CASES.CONVERSATION.CREATE')"
        :aria-label="$t('SUPPORT_CASES.CONVERSATION.CREATE')"
        data-testid="create-case-button"
        icon="i-lucide-ticket"
        ghost
        slate
        sm
        @click="openCaseCreateDialog"
      />
      <ConversationPriorityMenu />
      <ConversationSnoozeMenu />
      <MoreActions :conversation-id="currentChat.id" />
      <NextButton
        v-if="showOpenSidepanelButton"
        v-tooltip="$t('CONVERSATION.SIDEBAR.DETAILS')"
        icon="i-lucide-panel-right"
        ghost
        slate
        sm
        @click="openSidepanel"
      />
    </div>
    <CreateLinkedTicketDialog ref="createTicketDialogRef" />
    <CaseCreateDialog
      v-if="isSupportCasesEnabled"
      ref="caseCreateDialogRef"
      :case-types="caseTypes"
      :is-submitting="isCreatingCase"
      :initial-title="caseInitialTitle"
      :requester-contact="caseRequesterContact"
      @submit="onCaseCreate"
    />
  </div>
</template>
